// Minor league season, statistical (no box scores, no play-by-play, nothing to manage). Each regular season major
// league game between two organizations is also ONE minor league game between their affiliates, simulated by
// minorsGame.baseball.ts: the two rosters really face each other, so a strong lineup hits less against a strong staff,
// and both sides' numbers come from the same events. One compact line per player and season (p.minorStats) and one
// record per organization and season (teamSeason.minorLeague, with the processed game ids) are stored.
//
// Development: the line's playing time is turned into a small experience effect that rides on the existing annual
// progression (same mechanism as coaching, see developSeason.baseball.ts). Stats themselves never add ratings.
import { PLAYER, PHASE } from "../../../common/constants.ts";
import type { MinorStanding, MinorStats, Player } from "../../../common/types.ts";
import { idb } from "../../db/index.ts";
import { g } from "../../util/index.ts";
import { emptyStanding, getContext, simMinorsGame } from "./minorsGame.baseball.ts";

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));

export type MajorLeagueGame = { homeTid: number; awayTid: number; gid?: number };

/**
 * Minor league games for a batch of major league games (called as each batch of results is saved): same matchups,
 * off days and doubleheaders as the big clubs, regular season only. A game id already in the home affiliate's record
 * is skipped, so a game is never played twice (reload, partial day, repeated call). "all" (tests only) pairs the
 * organizations by tid and plays one round.
 */
export const simMinorsDay = async (games: MajorLeagueGame[] | "all" = "all") => {
	const phase = g.get("phase");
	if (phase !== PHASE.REGULAR_SEASON && phase !== PHASE.AFTER_TRADE_DEADLINE) {
		return;
	}
	const season = g.get("season");
	let matchups: MajorLeagueGame[];
	if (games === "all") {
		const tids = (await idb.cache.teams.getAll()).filter((t) => !t.disabled).map((t) => t.tid).sort((a, b) => a - b);
		matchups = [];
		for (let i = 0; i + 1 < tids.length; i += 2) matchups.push({ homeTid: tids[i]!, awayTid: tids[i + 1]! });
	} else {
		// All-Star and other special games use negative tids: no minor league game for them
		matchups = games.filter((x) => x.homeTid >= 0 && x.awayTid >= 0);
	}
	if (matchups.length === 0) {
		return;
	}
	const all = (await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS)).filter((p) => p.minorTid !== undefined);
	// Injured players don't play (and don't gain experience)
	const healthy = all.filter((p) => p.injury.gamesRemaining === 0);
	const ctx = getContext(healthy);
	const byOrg = Map.groupBy(healthy, (p) => p.minorTid!);

	// Records live on the organizations' team seasons; created on first use. If the big club already has games,
	// the season was under way when the feature started: the record covers only the games from then on.
	const teamSeasons = new Map<number, any>();
	const standingOf = async (tid: number, gamesInBatch: number): Promise<MinorStanding> => {
		let ts = teamSeasons.get(tid);
		if (!ts) {
			ts = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [tid, season]);
			if (!ts) {
				// No team season (shouldn't happen): play the game, keep no record
				return emptyStanding(false);
			}
			teamSeasons.set(tid, ts);
		}
		if (!ts.minorLeague) {
			const played = (ts.won ?? 0) + (ts.lost ?? 0) + (ts.tied ?? 0) + (ts.otl ?? 0);
			ts.minorLeague = emptyStanding(played > gamesInBatch);
		}
		return ts.minorLeague;
	};
	const gamesInBatch = new Map<number, number>();
	for (const m of matchups) for (const tid of [m.homeTid, m.awayTid]) gamesInBatch.set(tid, (gamesInBatch.get(tid) ?? 0) + 1);

	const touched = new Set<Player>();
	for (const m of matchups) {
		const home = await standingOf(m.homeTid, gamesInBatch.get(m.homeTid)!);
		const away = await standingOf(m.awayTid, gamesInBatch.get(m.awayTid)!);
		if (m.gid !== undefined) {
			if (home.gids.includes(m.gid)) continue;
			home.gids.push(m.gid);
			away.gids.push(m.gid);
		}
		const result = simMinorsGame({
			ctx,
			season,
			sides: [
				{ tid: m.homeTid, players: byOrg.get(m.homeTid) ?? [] },
				{ tid: m.awayTid, players: byOrg.get(m.awayTid) ?? [] },
			],
			standings: [home, away],
		});
		for (const p of result.touched) touched.add(p);
	}

	for (const p of touched) {
		await idb.cache.players.put(p);
	}
	for (const ts of teamSeasons.values()) {
		await idb.cache.teamSeasons.put(ts);
	}
};

/** Playing-time experience in [0, 1), diminishing returns (≈0.9 for a full season of regular play). */
export const minorsExperience = (p: { minorStats?: MinorStats }, season: number) => {
	const s = p.minorStats?.season === season ? p.minorStats : undefined;
	return 1 - Math.exp(-(s ? s.pa + s.bf : 0) / 250);
};

/**
 * Development: moderate effect of last season's minor league playing time, relative to the average minor leaguer
 * that season, so the feature as a whole adds no ratings (no inflation). Works like coaching on the annual
 * progression (scales the base change), so there is no second progression. Bounded to ±0.06; coaching is ±0.09.
 * Zero for anyone no longer in the minors (major leaguers develop exactly as before) and for everyone when no
 * minors season was simulated (old saves): then seasonMean is undefined.
 */
export const minorsExperienceEffect = (
	p: { tid: number; minorStats?: MinorStats },
	season: number,
	seasonMean: number | undefined,
) => {
	if (p.tid !== PLAYER.MINORS || seasonMean === undefined) {
		return 0;
	}
	return clamp(0.12 * (minorsExperience(p, season) - seasonMean), -0.06, 0.06);
};
